/**
 * ShiftDetailsDialog Component
 * Modal dialog for viewing and editing shift details.
 * Shows walkbook info, time, stats, instructions, and claimed volunteer.
 * Admin can edit shift properties or delete the shift.
 */

import { skip } from '@signalvote/data';
import { useState } from 'react';
import {
  MapPin,
  Calendar,
  Home,
  Clock,
  Flag,
  Footprints,
  MessageSquare,
  User,
  Pencil,
  Trash2,
  CheckCircle,
  PlayCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ShiftStatus, ShiftCalendarSyncStatus, isStaleWrite } from '@signalvote/shared';
import type { ShiftDto } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { GetTheApp } from '@/components/get-app';
import { Button } from '@/components/ui/button';
import {
  useShift,
  useUpdateShift,
  useDeleteShift,
  useClaimShift,
  useReleaseShift,
  useCompleteShift,
  useWalkbooks,
} from '@/hooks/queries';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignStore } from '@/stores/campaignStore';
import { CapabilityTier } from '@signalvote/shared';
import { formatTime12Hour, calculateEndTimeFromMinutes } from '@/lib/calendarUtils';

// ============================================================================
// Types
// ============================================================================

interface ShiftDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  shiftId: string | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDateForDisplay(dateStr: string): string {
  const date = parseISO(dateStr);
  return format(date, 'EEEE, MMMM d, yyyy');
}

function parseShiftStatus(status: string | ShiftStatus): ShiftStatus {
  if (typeof status === 'string') {
    return status as ShiftStatus;
  }
  return status;
}

function formatMinutesAsHours(minutes: number): string {
  const hours = minutes / 60;
  if (hours === Math.floor(hours)) {
    return `${hours}h`;
  }
  return `${hours.toFixed(1)}h`;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Shell: fetches the shift and owns the dialog frame. The body is remounted per
 * shift id, and Radix unmounts it on close, so the edit form is seeded exactly
 * once per "the user opened this shift" — a background refetch can no longer
 * reset the form or kick the user out of edit mode.
 */
export function ShiftDetailsDialog({
  open,
  onClose,
  shiftId,
}: ShiftDetailsDialogProps) {
  const shiftRead = useShift(shiftId ? shiftId : skip);
  const shift = shiftRead.status === 'ready' ? shiftRead.data : undefined;

  const showLoading = shiftRead.status === 'loading' && open;
  const showError = (shiftRead.status === 'failed' || shiftRead.status === 'offline') && open;
  // Nothing to put in a dialog: no shift loaded, and neither the spinner nor
  // the error message has anything to say.
  const hasNothingToShow = !showLoading && !showError && (!shift || !shiftId);
  if (hasNothingToShow) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        {showLoading ? (
          <>
            <DialogTitle className="sr-only">Loading shift details</DialogTitle>
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground">Loading shift details...</p>
            </div>
          </>
        ) : showError ? (
          <>
            <DialogTitle className="sr-only">Error</DialogTitle>
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="mt-4 text-sm text-muted-foreground">
                {shiftRead.status === 'failed' ? shiftRead.error.message : "You're offline, so this shift can't load."}
              </p>
              <Button variant="outline" onClick={onClose} className="mt-4">
                Close
              </Button>
            </div>
          </>
        ) : shift ? (
          <ShiftDetailsBody key={shift.id} shift={shift} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ShiftDetailsBody({ shift, onClose }: { shift: ShiftDto; onClose: () => void }) {
  const { user } = useAuth();
  const { hasCapability } = useCampaignStore();

  // Fetch walkbooks for edit mode dropdown
  const walkbooksRead = useWalkbooks(undefined);
  const walkbooks = walkbooksRead.status === 'ready' ? walkbooksRead.data : [];

  // Mutations
  const { mutate: updateShift, isPending: isSaving } = useUpdateShift();
  const [saveError, setSaveError] = useState<string | null>(null);
  const deleteShiftMutation = useDeleteShift();
  const claimShiftMutation = useClaimShift();
  const releaseShiftMutation = useReleaseShift();
  const completeShiftMutation = useCompleteShift();

  const [isEditing, setIsEditing] = useState(false);
  // Start Shift on the web opens a stop instead of starting: doors, contacts and the location check live in the app
  const [showAppStop, setShowAppStop] = useState(false);

  // Edit form state, seeded once from the shift this body was mounted for.
  const [name, setName] = useState(shift.name);
  const [date, setDate] = useState(shift.date);
  const [startTime, setStartTime] = useState(shift.startTime);
  const [estimatedMinutes, setEstimatedMinutes] = useState(shift.estimatedMinutes);
  const [walkbookId, setWalkbookId] = useState(shift.walkbookId);

  const status = parseShiftStatus(shift.status);
  const endTime = calculateEndTimeFromMinutes(shift.startTime, shift.estimatedMinutes);
  const timeRange = `${formatTime12Hour(shift.startTime)} - ${formatTime12Hour(endTime)}`;

  const isCompleted = status === ShiftStatus.Completed;
  const isClaimed = status === ShiftStatus.Claimed;
  const isInProgress = status === ShiftStatus.InProgress;
  const isAvailable = status === ShiftStatus.Available;
  const isCurrentUserClaimer = !!user && !!shift.claimedById && shift.claimedById === user.id;
  const canManageShifts = hasCapability('FieldOps', CapabilityTier.Manage);

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      deleteShiftMutation.mutate(shift.id, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  const handleSave = () => {
    setSaveError(null);
    updateShift(
      { row: shift, name, date, startTime, estimatedMinutes, walkbookId, assignedToId: null },
      {
        onSuccess: () => setIsEditing(false),
        onError: (error) =>
          setSaveError(
            isStaleWrite(error)
              ? 'This shift was changed by someone else after you opened it. Close it and open it again to see their changes, then redo your edit.'
              : error.message || "Couldn't save the shift. Try again.",
          ),
      },
    );
  };

  const handleCancelEdit = () => {
    // Reset form to current shift values
    setName(shift.name);
    setDate(shift.date);
    setStartTime(shift.startTime);
    setEstimatedMinutes(shift.estimatedMinutes);
    setWalkbookId(shift.walkbookId);
    setSaveError(null);
    setIsEditing(false);
  };

  const handleClaim = () => {
    claimShiftMutation.mutate(shift.id);
  };

  const handleRelease = () => {
    releaseShiftMutation.mutate(shift.id);
  };

  const handleComplete = () => {
    completeShiftMutation.mutate({ id: shift.id, data: { doorsKnocked: null, contactsMade: null } });
  };

  // Render completion stats for completed shifts
  const renderCompletionStats = () => {
    if (!shift.doorsKnocked && !shift.contactsMade) return null;

    const doorsKnocked = shift.doorsKnocked ?? 0;
    const contactsMade = shift.contactsMade ?? 0;

    return (
      <div className="mt-4">
        {/* Completion Header */}
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <div>
            <p className="font-semibold">Shift Completed</p>
            <p className="text-sm text-muted-foreground">
              Results from canvassing session
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-muted/50 p-3 rounded-md text-center">
            <p className="text-2xl font-bold">{doorsKnocked}</p>
            <p className="text-xs text-muted-foreground">Doors Knocked</p>
          </div>
          <div className="bg-muted/50 p-3 rounded-md text-center">
            <p className="text-2xl font-bold">{contactsMade}</p>
            <p className="text-xs text-muted-foreground">Contacts Made</p>
          </div>
        </div>
      </div>
    );
  };

  // Edit mode content
  if (isEditing) {
    return (
      <>
        <DialogTitle className="text-lg font-semibold">Edit Shift</DialogTitle>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          {/* Name */}
          <div className="col-span-2">
            <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Shift Name <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="e.g., Morning Downtown Canvass"
            />
          </div>

          {/* Walkbook Selection */}
          <div className="col-span-2">
            <label htmlFor="edit-walkbook" className="block text-sm font-medium text-gray-700 mb-1.5">
              Walkbook <span className="text-red-500">*</span>
            </label>
            <select
              id="edit-walkbook"
              value={walkbookId}
              onChange={(e) => setWalkbookId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">Select a walkbook...</option>
              {walkbooks.map((walkbook) => (
                <option key={walkbook.id} value={walkbook.id}>
                  {walkbook.name} ({walkbook.houseCount} houses)
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label htmlFor="edit-date" className="block text-sm font-medium text-gray-700 mb-1.5">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Start Time */}
          <div>
            <label htmlFor="edit-startTime" className="block text-sm font-medium text-gray-700 mb-1.5">
              Start Time <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-startTime"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Duration */}
          <div className="col-span-2">
            <label htmlFor="edit-duration" className="block text-sm font-medium text-gray-700 mb-1.5">
              Duration <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="edit-duration"
                type="number"
                value={estimatedMinutes}
                onChange={(e) => setEstimatedMinutes(Number(e.target.value))}
                min={30}
                max={480}
                step={15}
                className="w-full px-3 py-2 pr-20 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                minutes
              </span>
            </div>
          </div>
        </div>

        {saveError && (
          <p role="alert" className="text-sm text-destructive">
            {saveError}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name || !walkbookId || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </>
    );
  }

  // View mode content
  return (
    <>
      <div className="pt-2">
        {/* Header. pr-10 keeps the title clear of the dialog's ✕; the status
            badge sits under the title rather than in that same gutter. */}
        <div className="flex flex-col items-start gap-1.5 pr-10 mb-2">
          <DialogTitle className="text-xl font-bold leading-snug">{shift.name}</DialogTitle>
          <span className={`inline-flex w-fit items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isCompleted
              ? 'bg-green-100 text-green-800'
              : isInProgress
                ? 'bg-blue-100 text-blue-800'
                : isClaimed
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
          }`}>
            {status}
          </span>
        </div>

        {/* Walkbook Row */}
        {shift.walkbookName && (
          <div className="flex items-center gap-1.5 mb-4 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {shift.walkbookName}
          </div>
        )}

        {/* Time Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-md mb-4">
          <Calendar className="h-4 w-4" />
          <span className="text-sm font-medium">
            {formatDateForDisplay(shift.date)}, {timeRange}
          </span>
        </div>

        {/* Completion Stats (for completed shifts) */}
        {isCompleted && renderCompletionStats()}

        {/* Stats Row (hide for completed - shown in completion stats) */}
        {!isCompleted && (
          <>
            <hr className="my-4 border-border" />

            <div className="flex justify-around items-center">
              {/* Duration */}
              <div className="text-center">
                <Clock className="h-5 w-5 mx-auto text-muted-foreground" />
                <p className="text-lg font-bold">~{formatMinutesAsHours(shift.estimatedMinutes)}</p>
                <p className="text-xs text-muted-foreground">Duration</p>
              </div>

              <div className="h-12 w-px bg-border" />

              {/* Walkbook Houses (if available) */}
              <div className="text-center">
                <Home className="h-5 w-5 mx-auto text-muted-foreground" />
                <p className="text-lg font-bold">-</p>
                <p className="text-xs text-muted-foreground">Houses</p>
              </div>
            </div>

            <hr className="my-4 border-border" />

            {/* Instructions Section */}
            <h3 className="text-sm font-semibold mb-3">Instructions</h3>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2">
                <Flag className="h-4 w-4 text-primary mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Park on the main street and begin at the first house marked
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Footprints className="h-4 w-4 text-primary mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Work your way through the territory following the optimized route
                </p>
              </div>
              <div className="flex items-start gap-2">
                <MessageSquare className="h-4 w-4 text-primary mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Log each contact immediately after knocking
                </p>
              </div>
            </div>
          </>
        )}

        {/* Status Banners */}
        {isClaimed && shift.claimedByName && (
          <div className="flex items-center justify-center gap-2 mt-4 p-3 bg-green-100 rounded-md">
            <User className="h-4 w-4 text-green-700" />
            <span className="text-sm font-medium text-green-800">
              Claimed by {shift.claimedByName}
            </span>
          </div>
        )}

        {isInProgress && shift.claimedByName && (
          <div className="flex items-center justify-center gap-2 mt-4 p-3 border border-green-600 bg-green-100 rounded-md">
            <PlayCircle className="h-4 w-4 text-green-700" />
            <span className="text-sm font-medium text-green-800">
              Shift in progress - {shift.claimedByName}
            </span>
          </div>
        )}

        {/* Calendar sync status — the calendar write happens in the background, so the
            save is instant; this only surfaces while it's in flight or if it failed. */}
        {shift.calendarSyncStatus === ShiftCalendarSyncStatus.Pending && (
          <div className="flex items-center justify-center gap-2 mt-3 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>Syncing to calendar…</span>
          </div>
        )}
        {shift.calendarSyncStatus === ShiftCalendarSyncStatus.Failed && (
          <div className="flex items-center justify-center gap-2 mt-3 text-xs text-amber-600">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>Calendar sync failed — we&apos;ll keep retrying</span>
          </div>
        )}

        {/* Lifecycle Action Buttons */}
        {!showAppStop && (isAvailable || (isClaimed && isCurrentUserClaimer) || (isInProgress && isCurrentUserClaimer)) && (
          <div className="flex gap-2 mt-4">
            {isAvailable && (
              <Button
                onClick={handleClaim}
                disabled={claimShiftMutation.isPending}
                className="flex-1"
              >
                {claimShiftMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Claim Shift'
                )}
              </Button>
            )}
            {isClaimed && isCurrentUserClaimer && (
              <>
                <Button
                  variant="outline"
                  onClick={handleRelease}
                  disabled={releaseShiftMutation.isPending}
                  className="flex-1"
                >
                  {releaseShiftMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Release'
                  )}
                </Button>
                <Button onClick={() => setShowAppStop(true)} className="flex-1">
                  Start Shift
                </Button>
              </>
            )}
            {isInProgress && isCurrentUserClaimer && (
              <Button
                onClick={handleComplete}
                disabled={completeShiftMutation.isPending}
                className="flex-1"
              >
                {completeShiftMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Complete Shift'
                )}
              </Button>
            )}
          </div>
        )}

        {showAppStop && (
          <div ref={(el) => el?.scrollIntoView?.({ block: 'nearest' })} className="mt-4 rounded-lg border bg-muted/50 p-4">
            <p className="mb-2 text-center font-medium">Canvassing happens in the app</p>
            <GetTheApp />
            <Button variant="ghost" className="mt-2 w-full" onClick={() => setShowAppStop(false)}>
              Back
            </Button>
          </div>
        )}
      </div>

      <DialogFooter className="flex-row justify-between sm:justify-between">
        {canManageShifts ? (
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteShiftMutation.isPending}
            className="gap-1"
          >
            {deleteShiftMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Delete
          </Button>
        ) : (
          <div />
        )}
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {canManageShifts && (
            <Button
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="gap-1"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </DialogFooter>
    </>
  );
}

export default ShiftDetailsDialog;
